import { tz } from '@date-fns/tz'
import { addDays, addMonths } from 'date-fns'
import { z } from 'zod'

import { isoDateTime } from '../lib/date'
import { pluralizeUnit } from '../lib/text'

/**
 * Classifies the clinical event recorded in a treatment row.
 *
 *   `vaccination`  — Vaccine administered in-consult.
 *   `prescription` — Medication prescribed or dispensed. Operational details are
 *                    captured by the boolean snapshot flags (`administeredInConsult`,
 *                    `isExternal`, `isControlledSubstance`).
 *   `procedure`    — Service performed on the patient (e.g. dental, anal gland expression,
 *                    cremation). Creates a treatment record + dispensed order.
 *                    Future: services will decouple from the products table into their own
 *                    entity, with procedures generating constituent consumable orders.
 */
export const treatmentTypeSchema = z.enum(['vaccination', 'prescription', 'procedure'])

export type TreatmentType = z.infer<typeof treatmentTypeSchema>

export const refillIntervalSchema = z.discriminatedUnion('unit', [
  z.object({
    unit: z.literal('days'),
    amount: z
      .number()
      .int()
      .min(28, 'Refill interval must be at least 28 days')
      .max(180, 'Refill interval cannot exceed 180 days'),
  }),
  z.object({
    unit: z.literal('months'),
    amount: z.number().int().min(1).max(6, 'Refill interval cannot exceed 6 months'),
  }),
])

export type RefillInterval = z.infer<typeof refillIntervalSchema>

/** Prescription lifecycle and refill state, including supersession by other treatments for the same patient and product. */
export const treatmentPrescriptionContextSchema = z.object({
  /** Current, uncancelled prescription within its validity period, including one with exhausted refills. Internal prescriptions require authorised repeats. */
  isActivePrescription: z.boolean(),
  /** A pending, prepared or out-of-stock order exists for this treatment and blocks another refill request. */
  hasActiveOrder: z.boolean(),
  /**
   * Number of internal supplies beyond the initial supply, counted against repeatsAllowed.
   * Counts all orders except cancelled and returned ones, subtracts one for the initial supply, and floors at zero. In-flight orders count as supplies.
   * This records order usage, independently of prescription expiry, cancellation and supersession.
   */
  refillsUsed: z.number().int(),
  /**
   * Scheduled unlock time for the next unused refill: prescription date plus (refillsUsed + 1) intervals, less five days.
   * Calculated independently of outstanding orders; a past date means the cooldown has elapsed.
   * Null for inactive prescriptions or treatments without internal refills. This date can fall after prescription expiry.
   */
  refillCooldownEndsAt: isoDateTime.nullable(),
  /**
   * Whether another refill can be requested at the time this context was calculated.
   * Combines prescription validity, supersession, product restrictions, remaining allowance, active orders and the refill schedule.
   */
  isEligibleForRefill: z.boolean(),
})

export type TreatmentPrescriptionContext = z.output<typeof treatmentPrescriptionContextSchema>

/**
 * The context in which a treatment is prescribed - determines which product
 * types are available, how dispensing works, and which form fields are shown.
 *
 *   `clinic_consultation`  — Prescribed during an in-clinic consultation.
 *   `remote_consultation`  — Prescribed during a remote consultation.
 *   `standalone`           — Prescribed outside a consultation (e.g. the
 *                            quick-prescribe panel on the patient page).
 */
export type PrescribingContext = 'clinic_consultation' | 'remote_consultation' | 'standalone'

export const treatmentSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  consultationId: z.uuid().nullable(),
  /** Null for external prescriptions — use `catalogProductId` instead. */
  productId: z.uuid().nullable(),
  /** References the universal product catalog. Null when `productId` is set (inventory product). */
  catalogProductId: z.uuid().nullable(),
  treatmentType: treatmentTypeSchema,
  staffId: z.uuid(),
  quantity: z.number(),
  /**
   * Null = not applicable (in-consult types).
   * 0 = single fill only.
   * N = N additional fills permitted.
   */
  repeatsAllowed: z.number().int().nullable(),
  refillInterval: refillIntervalSchema.nullable(),
  /**
   * Client-facing instructions — shown on prescription labels, discharge notes, etc..
   * Keep language plain and owner-directed (e.g. "Give 1 tablet twice daily with food").
   */
  instructions: z.string().nullable(),
  /**
   * Clinical reasoning — diagnosis, rationale, patient context. Captured for standalone
   * prescriptions where there is no consultation to hold this context. Shown to the
   * client (surfaced on the patient's clinical record alongside the prescription).
   */
  clinicalNotes: z.string().nullable(),
  /**
   * Operational/dispensing notes — e.g. "client collecting from Boots on High St".
   * Captured for standalone treatments (internal and external prescriptions);
   * hidden for consult-related treatments. Never shown to the client.
   */
  internalNotes: z.string().nullable(),
  /** Physical vaccine tag number (e.g. rabies tag) recorded at administration. Only set for vaccinations. */
  tagNumber: z.string().nullable(),
  /** Free-text body site where the vaccine was injected. Only set for vaccinations. */
  injectionSite: z.string().nullable(),
  /** Only set for prescription types. */
  validUntil: isoDateTime.nullable(),
  /**
   * Whether the medication/vaccine was administered in-consult by staff.
   * Drives immediate stock deduction rather than going through the dispensing queue.
   * Always true for vaccinations and procedures; user-toggleable for prescriptions.
   */
  administeredInConsult: z.boolean(),
  /**
   * Snapshotted from the inventory product at creation time — regulatory audit trail.
   * Null for catalog (external) products where controlled status is unknown.
   */
  isControlledSubstance: z.boolean().nullable(),
  /** Whether this prescription is fulfilled by an external pharmacy (no stock impact). */
  isExternal: z.boolean(),
  /** Whether the prescription is issued under the veterinary cascade. Only meaningful for external prescriptions. */
  veterinaryCascade: z.boolean(),
  /**
   * The vet who authorizes the prescription with their e-signature.
   * Null = wet script (requires physical signing). Non-null = e-signature script (future).
   */
  authorizingVetId: z.uuid().nullable(),
  /** When the treatment was cancelled. Null = not cancelled. */
  cancelledAt: isoDateTime.nullable(),
  /** The staff member who cancelled the treatment. */
  cancelledBy: z.uuid().nullable(),
  /** Reason for cancellation — e.g. "Superseded by new prescription", "Adverse reaction". */
  cancelledReason: z.string().nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})

export type Treatment = z.output<typeof treatmentSchema>

/** Resolved product display info — works for both inventory and catalog products. */
export const treatmentProductInfoSchema = z.object({
  /** Display name — includes formulation when available. */
  name: z.string(),
  /** Dispensable unit label, e.g. "tablet", "ml". Defaults to "unit" when unknown. */
  unit: z.string(),
  /** Formatted quantity with pluralized unit, e.g. "3 tablets", "1 ml". */
  quantityLabel: z.string(),
})

export type TreatmentProductInfo = z.output<typeof treatmentProductInfoSchema>

/** Backend-computed derived fields added to treatments in API responses. */
export const treatmentDerivedFieldsSchema = z.object({
  /** Whether the treatment has been committed — has multiple orders (refills) or its single order is committed. */
  isCommitted: z.boolean(),
  productInfo: treatmentProductInfoSchema,
})

export type TreatmentDerivedFields = z.output<typeof treatmentDerivedFieldsSchema>

export function addRefillIntervals(date: Date, interval: RefillInterval, count: number): Date {
  const duration = interval.amount * count
  const result =
    interval.unit === 'months'
      ? addMonths(date, duration, { in: tz('UTC') })
      : addDays(date, duration, { in: tz('UTC') })
  return new Date(result.getTime())
}

export function formatRefillInterval(interval: RefillInterval): string {
  return `${interval.amount} ${pluralizeUnit(interval.unit.slice(0, -1), interval.amount)}`
}

/**
 * Derives display info for a treatment's product — works for both inventory
 * and catalog products. Used by the backend when enriching API responses and
 * by the frontend for unsaved treatment items.
 */
export function getProductDisplayInfo(treatment: {
  quantity: number
  product?: { name: string; unit: string | null } | null
  catalogProduct?: { name: string; unit: string | null } | null
}): TreatmentProductInfo {
  const source = treatment.product ?? treatment.catalogProduct
  if (!source) throw new Error('Treatment has neither product nor catalogProduct')
  const unit = source.unit?.trim() || 'unit'
  return {
    name: source.name,
    unit,
    quantityLabel: `${treatment.quantity} ${pluralizeUnit(unit, treatment.quantity)}`,
  }
}

/**
 * Formats a vaccine's injection site and tag number into a single
 * "Site: … · Tag: …" label. Each value is trimmed and blank/whitespace-only
 * values are dropped; returns null when neither is set so callers can skip
 * rendering. Shared across every surface that displays vaccine details so the
 * formatting (and trimming) stays consistent.
 */
export function formatVaccineTagAndSite(fields: {
  injectionSite?: string | null
  tagNumber?: string | null
}): string | null {
  const site = fields.injectionSite?.trim()
  const tag = fields.tagNumber?.trim()
  const parts = [site && `Site: ${site}`, tag && `Tag: ${tag}`].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : null
}

/**
 * Whether a treatment is a vaccine. Checks the explicit treatment type and the
 * attached inventory product type — the product check catches messy migrated
 * data where the treatment wasn't typed as a vaccination.
 */
export function isVaccineTreatment(t: {
  treatmentType: TreatmentType
  product?: { type: string } | null
}): boolean {
  return t.treatmentType === 'vaccination' || t.product?.type === 'vaccine'
}

/** A single administered batch — the batch number and expiry date snapshotted on stock. */
export type VaccineBatch = {
  batchNumber: string | null
  expiryDate: string | null
}

/**
 * Collects the administered batch number + expiry date for a treatment from its
 * order allocations. Reversed allocations are skipped, and batches are de-duped
 * by (batchNumber, expiryDate) so the same batch isn't repeated across refills.
 */
export function getAdministeredBatches(t: {
  orders: {
    allocations: {
      reversedAt: string | Date | null
      stock: { batchNumber: string | null; expiryDate: string | null }
    }[]
  }[]
}): VaccineBatch[] {
  const seen = new Set<string>()
  const batches: VaccineBatch[] = []
  for (const order of t.orders) {
    for (const allocation of order.allocations) {
      if (allocation.reversedAt) continue
      const { batchNumber, expiryDate } = allocation.stock
      // Skip allocations with neither a batch number nor an expiry — nothing to show.
      if (!batchNumber && !expiryDate) continue
      const key = `${batchNumber}|${expiryDate}`
      if (seen.has(key)) continue
      seen.add(key)
      batches.push({ batchNumber, expiryDate })
    }
  }
  return batches
}
